import React, { useState, useEffect, useCallback } from 'react';
import './AccountRegistrationApprovals.css';
import SvgIcon from '../SvgIcon';

const AccountRegistrationApprovals = ({ adminUser, setToast }) => {
  const [activeTab, setActiveTab] = useState('pending');
  const [accounts, setAccounts] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, active: 0, reclassified: 0, rejected: 0, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  // Edit Learner Profile Modal State
  const [editingLearner, setEditingLearner] = useState(null);
  const [editLearnerForm, setEditLearnerForm] = useState({
    fullName: '',
    username: '',
    email: '',
    contact: '',
    position: '',
    gender: 'Prefer not to say',
    itBackground: false,
    experienceYears: '',
    organisationName: '',
    sector: ''
  });
  const [isSavingLearner, setIsSavingLearner] = useState(false);
  const [approvalsOrgList, setApprovalsOrgList] = useState([]);
  const [approvalsOrgSearchTerm, setApprovalsOrgSearchTerm] = useState('');
  const [isApprovalsOrgDropdownOpen, setIsApprovalsOrgDropdownOpen] = useState(false);
  const approvalsOrgDropdownRef = React.useRef(null);

  // Custom styled dropdown states for Gender, Sector, and Experience
  const [isApprovalsGenderDropdownOpen, setIsApprovalsGenderDropdownOpen] = useState(false);
  const approvalsGenderDropdownRef = React.useRef(null);
  const [isApprovalsSectorDropdownOpen, setIsApprovalsSectorDropdownOpen] = useState(false);
  const approvalsSectorDropdownRef = React.useRef(null);
  const [isApprovalsExpDropdownOpen, setIsApprovalsExpDropdownOpen] = useState(false);
  const approvalsExpDropdownRef = React.useRef(null);

  // Fetch all organisations for auto-matching in Edit Learner Profile
  useEffect(() => {
    const loadOrganisations = async () => {
      try {
        const res = await fetch('/api/organisations/all', {
          headers: { 'Accept': 'application/json' }
        });
        const data = await res.json();
        if (data.status === 'success' && Array.isArray(data.data)) {
          setApprovalsOrgList(data.data);
          return;
        }

        // Fallback to individual sectors if /all is unavailable
        const [govRes, privRes, studRes] = await Promise.allSettled([
          fetch('/api/organisations/government', { headers: { 'Accept': 'application/json' } }).then(r => r.json()),
          fetch('/api/organisations/private', { headers: { 'Accept': 'application/json' } }).then(r => r.json()),
          fetch('/api/organisations/student', { headers: { 'Accept': 'application/json' } }).then(r => r.json())
        ]);

        const combined = [];
        if (govRes.status === 'fulfilled' && govRes.value?.status === 'success' && Array.isArray(govRes.value.data)) {
          govRes.value.data.forEach(o => combined.push({ ...o, Sector: o.Sector || 'Government' }));
        }
        if (privRes.status === 'fulfilled' && privRes.value?.status === 'success' && Array.isArray(privRes.value.data)) {
          privRes.value.data.forEach(o => combined.push({ ...o, Sector: o.Sector || 'Private' }));
        }
        if (studRes.status === 'fulfilled' && studRes.value?.status === 'success' && Array.isArray(studRes.value.data)) {
          studRes.value.data.forEach(o => combined.push({ ...o, Sector: o.Sector || 'Student' }));
        }

        if (combined.length > 0) {
          setApprovalsOrgList(combined);
        }
      } catch (err) {
        console.error('Error loading organisations:', err);
      }
    };
    loadOrganisations();
  }, []);

  // Close custom dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (approvalsOrgDropdownRef.current && !approvalsOrgDropdownRef.current.contains(e.target)) {
        setIsApprovalsOrgDropdownOpen(false);
      }
      if (approvalsGenderDropdownRef.current && !approvalsGenderDropdownRef.current.contains(e.target)) {
        setIsApprovalsGenderDropdownOpen(false);
      }
      if (approvalsSectorDropdownRef.current && !approvalsSectorDropdownRef.current.contains(e.target)) {
        setIsApprovalsSectorDropdownOpen(false);
      }
      if (approvalsExpDropdownRef.current && !approvalsExpDropdownRef.current.contains(e.target)) {
        setIsApprovalsExpDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reject Modal State
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedLearnerForReject, setSelectedLearnerForReject] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

  // Action submitting state
  const [processingId, setProcessingId] = useState(null);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const url = `/api/admin/account-registrations/pending?tab=${activeTab}&search=${encodeURIComponent(searchTerm)}`;
      const res = await fetch(url, {
        headers: {
          'x-admin-role': adminUser?.role || 'Super Admin'
        }
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setAccounts(data.data || []);
        if (data.counts) setCounts(data.counts);
      } else {
        if (setToast) setToast({ type: 'error', msg: data.message || 'Failed to fetch accounts.' });
      }
    } catch (err) {
      console.error('Fetch account registrations error:', err);
      if (setToast) setToast({ type: 'error', msg: 'Failed to connect to the server.' });
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchTerm, adminUser, setToast]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleApprove = async (learner) => {
    if (!window.confirm(`Are you sure you want to approve and activate the account for ${learner.FullName}?`)) {
      return;
    }

    setProcessingId(learner.LearnerID);
    try {
      const res = await fetch(`/api/admin/account-registrations/${learner.LearnerID}/approve`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-role': adminUser?.role || 'Super Admin'
        },
        body: JSON.stringify({
          adminId: adminUser?.id || adminUser?.adminId || null,
          adminName: adminUser?.fullName || adminUser?.name || 'Administrator',
          adminRole: adminUser?.role || 'Super Admin',
          vettingNotes: 'Civil service eligibility verified and approved by CCoE Administration.'
        })
      });

      const data = await res.json();
      if (res.ok && data.status === 'success') {
        if (setToast) setToast({ type: 'success', msg: data.message || 'Account approved successfully.' });
        fetchAccounts();
      } else {
        if (setToast) setToast({ type: 'error', msg: data.message || 'Approval failed.' });
      }
    } catch (err) {
      console.error(err);
      if (setToast) setToast({ type: 'error', msg: 'Failed to connect to the server.' });
    } finally {
      setProcessingId(null);
    }
  };

  const handleOpenEditLearner = (l) => {
    setEditingLearner(l);
    setEditLearnerForm({
      fullName: l.FullName || '',
      username: l.Username || '',
      email: l.Email || '',
      contact: l.Contact || '',
      position: l.Position || '',
      gender: l.Gender || 'Prefer not to say',
      itBackground: !!l.ITBackground,
      experienceYears: l.ExperienceYears || 'Not Applicable',
      organisationName: l.OrganisationName || '',
      sector: l.Sector || ''
    });
    setApprovalsOrgSearchTerm(l.OrganisationName || '');
    setIsApprovalsOrgDropdownOpen(false);
  };

  const handleSaveLearnerProfile = async (e) => {
    e.preventDefault();
    if (!editingLearner) return;
    setIsSavingLearner(true);
    try {
      const res = await fetch(`/api/admin/learners/${editingLearner.LearnerID}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'x-admin-role': adminUser?.role || 'Super Admin'
        },
        body: JSON.stringify(editLearnerForm)
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        if (setToast) setToast({ type: 'success', msg: data.message || 'Learner profile updated successfully!' });
        setEditingLearner(null);
        fetchAccounts();
      } else {
        if (setToast) setToast({ type: 'error', msg: data.message || 'Failed to update learner profile.' });
      }
    } catch (err) {
      console.error(err);
      if (setToast) setToast({ type: 'error', msg: 'Failed to connect to server.' });
    } finally {
      setIsSavingLearner(false);
    }
  };

  const handleRejectSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLearnerForReject) return;
    if (!rejectReason.trim()) {
      alert('Please provide an administrative reason for rejection.');
      return;
    }

    setRejectSubmitting(true);
    try {
      const res = await fetch(`/api/admin/account-registrations/${selectedLearnerForReject.LearnerID}/reject`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-role': adminUser?.role || 'Super Admin'
        },
        body: JSON.stringify({
          adminId: adminUser?.id || adminUser?.adminId || null,
          adminName: adminUser?.fullName || adminUser?.name || 'Administrator',
          vettingNotes: rejectReason.trim()
        })
      });

      const data = await res.json();
      if (res.ok && data.status === 'success') {
        if (setToast) setToast({ type: 'success', msg: data.message || 'Registration marked as rejected.' });
        setRejectModalOpen(false);
        setSelectedLearnerForReject(null);
        setRejectReason('');
        fetchAccounts();
      } else {
        if (setToast) setToast({ type: 'error', msg: data.message || 'Rejection failed.' });
      }
    } catch (err) {
      console.error(err);
      if (setToast) setToast({ type: 'error', msg: 'Failed to connect to the server.' });
    } finally {
      setRejectSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="account-approvals-workspace" data-testid="account-registration-workspace">
      <div className="account-approvals-card">
        
        {/* Workspace Sub-header */}
        <div className="account-approvals-header">
          <div className="account-approvals-title">
            <h3 style={{ margin: 0 }}>Account Registration</h3>
            <p style={{ margin: '4px 0 0 0' }}>48-hour business-day civil service verification queue and agency reclassification console.</p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div className="account-approvals-search-box">
              <SvgIcon name="search" size={16} />
              <input
                type="text"
                placeholder="Search name, email, agency..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="account-approvals-search"
              />
            </div>
            <button
              className="admin-header-action-btn admin-btn-outline"
              onClick={fetchAccounts}
              title="Refresh list"
              data-testid="refresh-account-approvals-btn"
            >
              <SvgIcon name="refresh" size={14} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Tabs for Pending, Active, Reclassified, Rejected */}
        <div className="account-tabs-bar" role="tablist">
          <button
            type="button"
            className={`account-tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
            data-testid="tab-pending-approvals"
          >
            <span>Pending Approvals</span>
            <span className="account-tab-badge">{counts.pending}</span>
          </button>

          <button
            type="button"
            className={`account-tab-btn ${activeTab === 'reclassified' ? 'active' : ''}`}
            onClick={() => setActiveTab('reclassified')}
            data-testid="tab-reclassified-accounts"
          >
            <span>Reclassified / Flagged</span>
            <span className="account-tab-badge">{counts.reclassified}</span>
          </button>

          <button
            type="button"
            className={`account-tab-btn ${activeTab === 'rejected' ? 'active' : ''}`}
            onClick={() => setActiveTab('rejected')}
            data-testid="tab-rejected-accounts"
          >
            <span>Rejected</span>
            <span className="account-tab-badge">{counts.rejected}</span>
          </button>
        </div>

        {/* Table of Records */}
        <div className="account-table-wrapper">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
              <div className="profile-loading-spinner" style={{ margin: '0 auto 1rem auto' }} />
              Loading registration records...
            </div>
          ) : accounts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3.5rem 1rem', color: '#64748b' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem auto', color: '#94a3b8' }}>
                <SvgIcon name="users" size={24} />
              </div>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#1e293b', fontSize: '1rem' }}>
                {activeTab === 'pending' ? 'No pending account approvals.' : 'No account records found.'}
              </h4>
              <p style={{ margin: 0, fontSize: '0.85rem' }}>
                {activeTab === 'pending'
                  ? 'All government civil service applications have been vetted and processed.'
                  : 'No accounts match the current filter selection.'}
              </p>
            </div>
          ) : (
            <table className="account-approvals-table" data-testid="account-approvals-table">
              <thead>
                <tr>
                  <th>Learner Profile</th>
                  <th>Email</th>
                  <th>Agency / Organisation</th>
                  <th>Supervisor Details</th>
                  <th>Submission Date</th>
                  <th>SLA Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((row) => (
                  <tr key={row.LearnerID} data-testid={`account-row-${row.LearnerID}`}>
                    
                    {/* Learner Profile */}
                    <td>
                      <div className="learner-profile-cell">
                        <span className="learner-profile-name">{row.FullName}</span>
                        <span className="learner-profile-meta">
                          @{row.Username} · {row.Gender || 'N/A'} {row.Position ? `· ${row.Position}` : ''}
                        </span>
                        {row.ReclassifiedFrom && (
                          <span style={{ fontSize: '0.7rem', color: '#b45309', fontWeight: 600 }}>
                            Reclassified from: {row.ReclassifiedFrom}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Email */}
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ color: '#0f172a', fontWeight: 500 }}>{row.Email}</span>
                        {row.Contact && <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{row.Contact}</span>}
                      </div>
                    </td>

                    {/* Agency / Organisation */}
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <strong style={{ color: '#1e293b' }}>{row.OrganisationName || 'Not Specified'}</strong>
                        <span style={{
                          display: 'inline-block',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          padding: '1px 6px',
                          borderRadius: '4px',
                          width: 'fit-content',
                          marginTop: '2px',
                          backgroundColor: row.Sector === 'Government' ? '#e0f2fe' : row.Sector === 'Private' ? '#f3e8ff' : '#f1f5f9',
                          color: row.Sector === 'Government' ? '#0369a1' : row.Sector === 'Private' ? '#6b21a8' : '#475569'
                        }}>
                          {row.Sector || 'General'}
                        </span>
                      </div>
                    </td>

                    {/* Supervisor Details */}
                    <td>
                      {row.SupervisorName || row.SupervisorEmail ? (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: 600, color: '#1e293b' }}>{row.SupervisorName || 'Supervisor'}</span>
                          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{row.SupervisorEmail || 'No email'}</span>
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>N/A</span>
                      )}
                    </td>

                    {/* Submission Date */}
                    <td>
                      <span style={{ color: '#334155' }}>
                        {formatDate(row.SlaDeadline ? new Date(new Date(row.SlaDeadline).getTime() - 48*60*60*1000) : null)}
                      </span>
                    </td>

                    {/* Dynamic SLA Badge */}
                    <td>
                      {(() => {
                        const badgeClass = row.slaBadgeClass || (row.slaMetrics?.status === 'red' ? 'badge-red overdue-pulse' : row.slaMetrics?.status === 'amber' ? 'badge-amber' : row.slaMetrics?.status === 'green' ? 'badge-green' : 'badge-gray');
                        const badgeText = row.slaBadgeText || row.slaMetrics?.badgeText || 'N/A';
                        return (
                          <div className="sla-badge-container">
                            <span className={`sla-badge ${badgeClass}`} data-testid={`sla-badge-${row.LearnerID}`}>
                              <SvgIcon name="calendar" size={12} />
                              {badgeText}
                            </span>
                            {row.slaSubText && <span className="sla-subtext">{row.slaSubText}</span>}
                          </div>
                        );
                      })()}
                    </td>

                    {/* Actions */}
                    <td>
                      <div className="account-action-buttons">
                        {row.AccountStatus === 'Pending Verification' && (
                          <button
                            type="button"
                            className="btn-row-approve"
                            onClick={() => handleApprove(row)}
                            disabled={processingId === row.LearnerID}
                            data-testid={`approve-btn-${row.LearnerID}`}
                          >
                            Approve
                          </button>
                        )}

                        <button
                          type="button"
                          className="btn-action-edit"
                          onClick={() => handleOpenEditLearner(row)}
                          title="Edit Learner Profile"
                          data-testid={`edit-btn-${row.LearnerID}`}
                        >
                          <SvgIcon name="edit" size={13} /> Edit
                        </button>

                        {row.AccountStatus === 'Pending Verification' && (
                          <button
                            type="button"
                            className="btn-row-reject"
                            onClick={() => {
                              setSelectedLearnerForReject(row);
                              setRejectReason('');
                              setRejectModalOpen(true);
                            }}
                            data-testid={`reject-btn-${row.LearnerID}`}
                          >
                            Reject
                          </button>
                        )}
                      </div>
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>

      {/* Edit Learner Profile Modal */}
      {editingLearner && (
        <div 
          className="modal-overlay" 
          onClick={(e) => { if (e.target === e.currentTarget) setEditingLearner(null); }}
          data-testid="edit-learner-modal"
        >
          <div className="modal-content" style={{ maxWidth: '640px', width: '92%', maxHeight: '90vh', overflow: 'visible' }}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255, 255, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff' }}>
                  <SvgIcon name="edit" size={18} />
                </div>
                <div style={{ textAlign: 'left' }}>
                  <h2 style={{ fontSize: '1.1rem', margin: 0, color: '#ffffff' }}>Edit Learner Profile</h2>
                  <span style={{ fontSize: '0.78rem', color: '#c7d2fe' }}>Update participant profile information</span>
                </div>
              </div>
              <button 
                type="button"
                className="modal-close" 
                onClick={() => setEditingLearner(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveLearnerProfile} style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem', textAlign: 'left' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                <div className="modal-form-field">
                  <label className="modal-label" htmlFor="edit-fullname">Full Name</label>
                  <input
                    type="text"
                    id="edit-fullname"
                    className="modal-form-input"
                    value={editLearnerForm.fullName}
                    onChange={(e) => setEditLearnerForm(prev => ({ ...prev, fullName: e.target.value }))}
                    required
                    data-testid="edit-learner-fullname"
                  />
                </div>
                <div className="modal-form-field">
                  <label className="modal-label" htmlFor="edit-username">Username</label>
                  <input
                    type="text"
                    id="edit-username"
                    className="modal-form-input"
                    value={editLearnerForm.username}
                    onChange={(e) => setEditLearnerForm(prev => ({ ...prev, username: e.target.value }))}
                    data-testid="edit-learner-username"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                <div className="modal-form-field">
                  <label className="modal-label" htmlFor="edit-email">Email Address</label>
                  <input
                    type="email"
                    id="edit-email"
                    className="modal-form-input"
                    value={editLearnerForm.email}
                    onChange={(e) => setEditLearnerForm(prev => ({ ...prev, email: e.target.value }))}
                    required
                    data-testid="edit-learner-email"
                  />
                </div>
                <div className="modal-form-field">
                  <label className="modal-label" htmlFor="edit-contact">Contact Number</label>
                  <input
                    type="text"
                    id="edit-contact"
                    className="modal-form-input"
                    value={editLearnerForm.contact}
                    onChange={(e) => setEditLearnerForm(prev => ({ ...prev, contact: e.target.value }))}
                    data-testid="edit-learner-contact"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                <div className="modal-form-field">
                  <label className="modal-label" htmlFor="edit-position">Position / Job Title</label>
                  <input
                    type="text"
                    id="edit-position"
                    className="modal-form-input"
                    value={editLearnerForm.position}
                    onChange={(e) => setEditLearnerForm(prev => ({ ...prev, position: e.target.value }))}
                    data-testid="edit-learner-position"
                  />
                </div>
                <div className="modal-form-field" style={{ position: 'relative' }} ref={approvalsOrgDropdownRef}>
                  <label className="modal-label" htmlFor="edit-org">Organisation Name</label>
                  <input
                    type="text"
                    id="edit-org"
                    className="modal-form-input"
                    placeholder="Search or enter organisation..."
                    value={isApprovalsOrgDropdownOpen ? approvalsOrgSearchTerm : (editLearnerForm.organisationName || approvalsOrgSearchTerm)}
                    onChange={(e) => {
                      const val = e.target.value;
                      setApprovalsOrgSearchTerm(val);
                      setIsApprovalsOrgDropdownOpen(true);
                      setEditLearnerForm(prev => ({ ...prev, organisationName: val }));

                      // Check for immediate exact or acronym match to auto-set sector
                      if (val.trim()) {
                        const cleanTerm = val.trim().toLowerCase();
                        const matched = approvalsOrgList.find(o =>
                          (o.OrganisationName && o.OrganisationName.toLowerCase() === cleanTerm) ||
                          (o.OrganisationName && o.OrganisationName.toLowerCase().includes(`(${cleanTerm})`))
                        );
                        if (matched && matched.Sector) {
                          setEditLearnerForm(prev => ({ ...prev, sector: matched.Sector }));
                        }
                      }
                    }}
                    onFocus={() => {
                      setApprovalsOrgSearchTerm(editLearnerForm.organisationName || '');
                      setIsApprovalsOrgDropdownOpen(true);
                    }}
                    data-testid="edit-learner-org"
                  />
                  {isApprovalsOrgDropdownOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        background: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
                        maxHeight: '220px',
                        overflowY: 'auto',
                        zIndex: 9999
                      }}
                    >
                      {(() => {
                        const term = (approvalsOrgSearchTerm || '').trim().toLowerCase();
                        let filteredOrgs = [];

                        if (term) {
                          const words = term.split(/\s+/).filter(Boolean);

                          filteredOrgs = approvalsOrgList
                            .map(org => {
                              const name = (org.OrganisationName || '').toLowerCase();
                              const domain = (org.EmailDomain || '').toLowerCase();
                              
                              // Check name direct inclusion or domain inclusion
                              const nameMatch = name.includes(term);
                              const domainMatch = domain && (domain.includes(term) || term.includes(domain.split('.')[0]));
                              
                              // Check if all search words are present in name or domain
                              const allWordsMatch = words.every(w => name.includes(w) || (domain && domain.includes(w)));
                              
                              // Check acronym/parentheses match e.g. "(jpj)" or "(maybank)"
                              const acronymMatch = name.includes(`(${term})`) || name.startsWith(term);

                              if (!nameMatch && !domainMatch && !allWordsMatch) return null;

                              let score = 0;
                              if (acronymMatch) score += 60;
                              if (nameMatch) score += 40;
                              if (domainMatch) score += 35;
                              if (name.startsWith(term)) score += 25;
                              if (allWordsMatch) score += 15;

                              return { org, score };
                            })
                            .filter(Boolean)
                            .sort((a, b) => b.score - a.score)
                            .map(item => item.org);
                        } else {
                          filteredOrgs = approvalsOrgList.slice(0, 40);
                        }

                        const handleSelectOrg = (org) => {
                          setEditLearnerForm(prev => ({
                            ...prev,
                            organisationName: org.OrganisationName,
                            sector: org.Sector || prev.sector || 'Government'
                          }));
                          setApprovalsOrgSearchTerm(org.OrganisationName);
                          setIsApprovalsOrgDropdownOpen(false);
                        };

                        if (filteredOrgs.length === 0) {
                          return (
                            <div style={{ padding: '0.75rem 1rem', fontSize: '0.85rem', color: '#64748b' }}>
                              No organisations found matching &quot;{approvalsOrgSearchTerm}&quot;
                            </div>
                          );
                        }

                        return (
                          <>
                            {filteredOrgs.slice(0, 60).map(org => (
                              <div
                                key={org.OrganisationID}
                                style={{
                                  padding: '0.65rem 1rem',
                                  fontSize: '0.85rem',
                                  color: '#1e293b',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  gap: '0.75rem',
                                  background: editLearnerForm.organisationName === org.OrganisationName ? '#f0fdf4' : 'transparent',
                                  transition: 'background-color 0.15s ease'
                                }}
                                onMouseEnter={(e) => {
                                  if (editLearnerForm.organisationName !== org.OrganisationName) {
                                    e.currentTarget.style.backgroundColor = '#f8fafc';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (editLearnerForm.organisationName !== org.OrganisationName) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }
                                }}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  handleSelectOrg(org);
                                }}
                              >
                                <span style={{ fontWeight: 500, flex: 1, textAlign: 'left' }}>{org.OrganisationName}</span>
                                <span style={{
                                  fontSize: '0.7rem',
                                  padding: '2px 8px',
                                  borderRadius: '10px',
                                  fontWeight: '600',
                                  flexShrink: 0,
                                  background: org.Sector === 'Government' ? '#dcfce7' : org.Sector === 'Private' ? '#e0e7ff' : '#fef3c7',
                                  color: org.Sector === 'Government' ? '#15803d' : org.Sector === 'Private' ? '#4338ca' : '#b45309'
                                }}>
                                  {org.Sector}
                                </span>
                              </div>
                            ))}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                <div className="modal-form-field" style={{ position: 'relative' }} ref={approvalsGenderDropdownRef}>
                  <label className="modal-label" htmlFor="edit-gender">Gender</label>
                  <div
                    className="modal-form-input"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      userSelect: 'none',
                      backgroundColor: isApprovalsGenderDropdownOpen ? '#ffffff' : '#f8fafc',
                      borderColor: isApprovalsGenderDropdownOpen ? '#4f46e5' : '#cbd5e1',
                      boxShadow: isApprovalsGenderDropdownOpen ? '0 0 0 3px rgba(79, 70, 229, 0.15)' : 'none'
                    }}
                    onClick={() => setIsApprovalsGenderDropdownOpen(prev => !prev)}
                  >
                    <input
                      id="edit-gender"
                      readOnly
                      value={editLearnerForm.gender || 'Select Gender'}
                      data-testid="edit-learner-gender"
                      style={{
                        border: 'none',
                        background: 'transparent',
                        outline: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        margin: 0,
                        width: '100%',
                        fontSize: '0.875rem',
                        color: editLearnerForm.gender ? '#0f172a' : '#64748b'
                      }}
                    />
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#64748b"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        transform: isApprovalsGenderDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                        flexShrink: 0
                      }}
                    >
                      <path d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {isApprovalsGenderDropdownOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        background: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
                        zIndex: 9999,
                        overflow: 'hidden'
                      }}
                    >
                      {['Male', 'Female', 'Prefer not to say'].map(opt => (
                        <div
                          key={opt}
                          style={{
                            padding: '0.65rem 1rem',
                            fontSize: '0.85rem',
                            color: '#1e293b',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f1f5f9',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            background: editLearnerForm.gender === opt ? '#f0fdf4' : 'transparent',
                            fontWeight: editLearnerForm.gender === opt ? 600 : 400,
                            transition: 'background-color 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (editLearnerForm.gender !== opt) e.currentTarget.style.backgroundColor = '#f8fafc';
                          }}
                          onMouseLeave={(e) => {
                            if (editLearnerForm.gender !== opt) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setEditLearnerForm(prev => ({ ...prev, gender: opt }));
                            setIsApprovalsGenderDropdownOpen(false);
                          }}
                        >
                          <span>{opt}</span>
                          {editLearnerForm.gender === opt && (
                            <span style={{ color: '#15803d', fontSize: '0.85rem', fontWeight: 'bold' }}>✓</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="modal-form-field" style={{ position: 'relative' }} ref={approvalsSectorDropdownRef}>
                  <label className="modal-label" htmlFor="edit-sector">Sector</label>
                  <div
                    className="modal-form-input"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      userSelect: 'none',
                      backgroundColor: isApprovalsSectorDropdownOpen ? '#ffffff' : '#f8fafc',
                      borderColor: isApprovalsSectorDropdownOpen ? '#4f46e5' : '#cbd5e1',
                      boxShadow: isApprovalsSectorDropdownOpen ? '0 0 0 3px rgba(79, 70, 229, 0.15)' : 'none'
                    }}
                    onClick={() => setIsApprovalsSectorDropdownOpen(prev => !prev)}
                  >
                    <input
                      id="edit-sector"
                      readOnly
                      value={editLearnerForm.sector || 'Select Sector'}
                      data-testid="edit-learner-sector"
                      style={{
                        border: 'none',
                        background: 'transparent',
                        outline: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        margin: 0,
                        width: '100%',
                        fontSize: '0.875rem',
                        color: editLearnerForm.sector ? '#0f172a' : '#64748b'
                      }}
                    />
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#64748b"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        transform: isApprovalsSectorDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                        flexShrink: 0
                      }}
                    >
                      <path d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {isApprovalsSectorDropdownOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        background: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
                        zIndex: 9999,
                        overflow: 'hidden'
                      }}
                    >
                      {['Government', 'Private', 'Student'].map(sec => (
                        <div
                          key={sec}
                          style={{
                            padding: '0.65rem 1rem',
                            fontSize: '0.85rem',
                            color: '#1e293b',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f1f5f9',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            background: editLearnerForm.sector === sec ? '#f0fdf4' : 'transparent',
                            fontWeight: editLearnerForm.sector === sec ? 600 : 400,
                            transition: 'background-color 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (editLearnerForm.sector !== sec) e.currentTarget.style.backgroundColor = '#f8fafc';
                          }}
                          onMouseLeave={(e) => {
                            if (editLearnerForm.sector !== sec) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setEditLearnerForm(prev => ({ ...prev, sector: sec }));
                            setIsApprovalsSectorDropdownOpen(false);
                          }}
                        >
                          <span>{sec}</span>
                          {editLearnerForm.sector === sec && (
                            <span style={{ color: '#15803d', fontSize: '0.85rem', fontWeight: 'bold' }}>✓</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                <div className="modal-form-field" style={{ position: 'relative' }} ref={approvalsExpDropdownRef}>
                  <label className="modal-label" htmlFor="edit-experience">Years of IT Experience</label>
                  <div
                    className="modal-form-input"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      userSelect: 'none',
                      backgroundColor: isApprovalsExpDropdownOpen ? '#ffffff' : '#f8fafc',
                      borderColor: isApprovalsExpDropdownOpen ? '#4f46e5' : '#cbd5e1',
                      boxShadow: isApprovalsExpDropdownOpen ? '0 0 0 3px rgba(79, 70, 229, 0.15)' : 'none'
                    }}
                    onClick={() => setIsApprovalsExpDropdownOpen(prev => !prev)}
                  >
                    <input
                      id="edit-experience"
                      readOnly
                      value={editLearnerForm.experienceYears || 'Not Applicable'}
                      data-testid="edit-learner-experience"
                      style={{
                        border: 'none',
                        background: 'transparent',
                        outline: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        margin: 0,
                        width: '100%',
                        fontSize: '0.875rem',
                        color: editLearnerForm.experienceYears ? '#0f172a' : '#64748b'
                      }}
                    />
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#64748b"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        transform: isApprovalsExpDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                        flexShrink: 0
                      }}
                    >
                      <path d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {isApprovalsExpDropdownOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        background: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
                        maxHeight: '135px',
                        overflowY: 'auto',
                        zIndex: 9999
                      }}
                    >
                      {['Not Applicable', 'Less than 1 Years', 'Between 1 to 5 Years', 'More than 5 Years'].map(opt => (
                        <div
                          key={opt}
                          style={{
                            padding: '0.65rem 1rem',
                            fontSize: '0.85rem',
                            color: '#1e293b',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f1f5f9',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            background: (editLearnerForm.experienceYears || 'Not Applicable') === opt ? '#f0fdf4' : 'transparent',
                            fontWeight: (editLearnerForm.experienceYears || 'Not Applicable') === opt ? 600 : 400,
                            transition: 'background-color 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            if ((editLearnerForm.experienceYears || 'Not Applicable') !== opt) e.currentTarget.style.backgroundColor = '#f8fafc';
                          }}
                          onMouseLeave={(e) => {
                            if ((editLearnerForm.experienceYears || 'Not Applicable') !== opt) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setEditLearnerForm(prev => ({ ...prev, experienceYears: opt }));
                            setIsApprovalsExpDropdownOpen(false);
                          }}
                        >
                          <span>{opt}</span>
                          {(editLearnerForm.experienceYears || 'Not Applicable') === opt && (
                            <span style={{ color: '#15803d', fontSize: '0.85rem', fontWeight: 'bold' }}>✓</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', paddingTop: '1.25rem', marginTop: '0.5rem', borderTop: '1px solid #e2e8f0' }}>
                <button
                  type="button"
                  style={{ padding: '0.6rem 1.25rem', background: '#ffffff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease' }}
                  onClick={() => setEditingLearner(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSavingLearner}
                  style={{ padding: '0.6rem 1.4rem', background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.25)' }}
                  data-testid="save-learner-changes-btn"
                >
                  {isSavingLearner ? 'Saving...' : 'Save Learner Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModalOpen && selectedLearnerForReject && (
        <div 
          className="admin-modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.7)',
            backdropFilter: 'blur(4px)',
            zIndex: 20000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
          data-testid="reject-reason-modal"
        >
          <div style={{ width: '100%', maxWidth: '480px', backgroundColor: '#ffffff', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: '#dc2626' }}>
                Reject Account Registration
              </h3>
              <button 
                type="button" 
                onClick={() => setRejectModalOpen(false)} 
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
              >
                <SvgIcon name="close" size={18} />
              </button>
            </div>
            
            <form onSubmit={handleRejectSubmit} style={{ padding: '1.5rem' }}>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.875rem', color: '#475569' }}>
                You are rejecting the registration for <strong>{selectedLearnerForReject.FullName}</strong> ({selectedLearnerForReject.Email}). Any uncommitted training course reservations will be released.
              </p>
              
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.4rem' }}>
                  Rejection Reason *
                </label>
                <textarea
                  rows={3}
                  required
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Specify why this registration was not approved..."
                  data-testid="reject-reason-textarea"
                  style={{ width: '100%', padding: '0.65rem', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => setRejectModalOpen(false)}
                  disabled={rejectSubmitting}
                  style={{ padding: '0.55rem 1rem', border: '1px solid #cbd5e1', borderRadius: '6px', backgroundColor: '#ffffff', color: '#475569', fontSize: '0.85rem', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={rejectSubmitting}
                  data-testid="confirm-reject-btn"
                  style={{ padding: '0.55rem 1.25rem', border: 'none', borderRadius: '6px', backgroundColor: '#dc2626', color: '#ffffff', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
                >
                  {rejectSubmitting ? 'Rejecting...' : 'Confirm Rejection'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default AccountRegistrationApprovals;
